import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Table, TableEditCompleteEvent, TableEditInitEvent, TableLazyLoadEvent } from 'primeng/table';
import { getIconAndTooltip } from '../../../modules/inventory/item-management-dto';
import { MarkdownService } from '../../markdown.service';

export interface TableAction {
	label: string;
	icon: string;
	callback: (rowData: any) => void;
}

export type TableActionWithSeverity = TableAction & {
	severity: 'success' | 'info' | 'warning' | 'primary' | 'help' | 'danger' | 'secondary' | 'contrast' | null;
};

@Component({
	selector: 'app-table',
	templateUrl: './table.component.html',
	styleUrls: ['./table.component.scss']
})
export class TableComponent {
	@Input() title?: string;
	@Input() errorMessage?: string;
	@Input() columns?: any[];
	@Input() data!: any[];
	@Input() rows: number | undefined;
	@Input() scrollable: boolean = false;
	@Input() scrollHeight?: string = '250px';
	@Input() rowsPerPageOptions: number[] = [20, 50, 100];
	@Input() loading: boolean = false;
	@Input() globalFilterFields: string[] = [];
	@Input() statusOptions: string[] = [];
	@ViewChild('table') table!: Table;

	@Input() editableFields: string[] = [];
	@Output() editInit = new EventEmitter<TableEditInitEvent>();
	@Output() editComplete = new EventEmitter<TableEditCompleteEvent>();

	@Input() imageColumns: string[] = [];

	/** Columns whose cell value is markdown to render as HTML (e.g. links). */
	@Input() htmlColumns: string[] = [];

	@Output() imgClicked = new EventEmitter<string>();

	constructor(private markdownService: MarkdownService) {}

	renderHtml(value: string): SafeHtml | string {
		return this.markdownService.processMarkdown(value);
	}

	/** paginator needs to  be set either in lazy mode or normal mode to enable pagination feature
 In normal mode, simply setting paginator to true combined with rows property to use the pagination feature.
 In lazy mode, paginator also need to be set to true, but each page needs to be laoded with onLazyLoad()
*/
	@Input() paginator: boolean = false;

	@Input() lazyMode: boolean = false;
	@Input() selectionMode?: 'single' | 'multiple' | undefined | null = null;
	@Input() selectedData: any[] = [];
	@Input() dataLength: number = 0;
	@Input() first: number = 0;

	@Output() lazyLoad = new EventEmitter<TableLazyLoadEvent>();
	@Output() sort = new EventEmitter<any>();
	@Output() menuShow = new EventEmitter<any>();
	/** TODO: still in development */
	@Input() actions: TableActionWithSeverity[] = [];
	@Input() filters: { [s: string]: any } = {};
	@Input() customFilterComponents: { [field: string]: 'dropdown' | 'input' } = {};
	@Input() dropdownOptions: { [field: string]: { label: string; value: any }[] } = {};

	@Input() enabledColFilters: boolean = true;
	@Input() styleClass: string = '';
	@Input() sortField: string = '';
	@Input() sortOrder: number = -1;

	ngOnInit(): void {
		this.scrollHeight = this.scrollable ? '250px' : undefined;
		for (const field of Object.keys(this.customFilterComponents)) {
			if (!this.filters[field]) {
				this.filters[field] = {
					value: null,
					matchMode: this.customFilterComponents[field] === 'dropdown' ? 'equals' : 'contains'
				};
			}
		}
	}

	onGlobalFilter(event: Event, value: string) {
		this.table.filterGlobal((event.target as HTMLInputElement).value, value);
	}

	onEditInit(event: TableEditInitEvent) {
		this.editInit.emit(event);
	}

	onEditComplete(event: TableEditCompleteEvent) {
		this.editComplete.emit(event);
	}

	onImgClicked(fullImgSrc: string) {
		this.imgClicked.emit(fullImgSrc);
	}

	getIconAndTooltip(field: string, value: string) {
		return getIconAndTooltip(field, value);
	}

	/** will only trigger under lazy mode */
	onLazyLoad(event: TableLazyLoadEvent) {
		this.lazyLoad.emit(event);
	}

	/** only execute onSort in lazy mode since sorting in normal mode
	 * is handled by primeng out of the box */
	onSort(event: any) {
		if (this.lazyMode) {
			this.sort.emit(event);
		}
	}

	onDropdownClear(field: string) {
		this.filters[field].value = null;
		this.table.filter(null, field, 'equals');
	}
}
